import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import memoize from 'memoize-one';
import { useAbility } from '@casl/react';
import { legalreportShiftReport } from '../../requests/legalreports';
import { ComponentDataTable, DefaultModal } from '../../components';
import { indexEmployeesRequest } from '../../requests/employees';
import { AbilityContext } from '../../config/abilityContext';
import { ShiftFilterDataTable } from '../../components/LegalReport';
import { downloadFile } from '../../services/utils';
import LegalReportFilter from './Filters/LegalReportFilter';
import ReportActions from './ReportActions';

const columns = memoize(() => [
  {
    name: 'RUT',
    selector: 'national_identification',
    sortable: true,
    grow: '1'
  },
  {
    name: 'NOMBRE',
    selector: 'name',
    cell: item => <>{item.full_name}</>,
    sortable: true,
    grow: '2'
  },
  {
    name: 'LUGAR DE PRESTACION DE SERVICIOS',
    selector: 'branch_office',
    sortable: false,
    grow: '3'
  }
]);

const recordsColumns = memoize(() => [
  {
    name: 'FECHA TURNO ORIGINAL',
    selector: 'original_shift_date',
    sortable: true,
    grow: '2'
  },
  {
    name: 'TURNO ORIGINAL',
    selector: 'original_turn_name',
    sortable: true,
    grow: '3'
  },
  {
    name: 'FECHA TURNO NUEVO',
    selector: 'new_shift_date',
    sortable: true,
    grow: '2'
  },
  {
    name: 'TURNO NUEVO',
    selector: 'new_turn_name',
    sortable: true,
    grow: '3'
  },
  {
    name: 'TIPO DE TURNO',
    selector: 'shift_type',
    sortable: true,
    grow: '2'
  },
  {
    name: 'SOLICITADOR',
    selector: 'solicitor',
    sortable: true,
    grow: '3'
  }
]);

export const MyExpander = props => {
  const { data, dateRange } = props;
  const { id } = data;

  return (
    <ShiftFilterDataTable
      columns={recordsColumns}
      customParams={{ filter_by_employee: id, date_range: dateRange }}
      props
    />
  );
};

export const LoadingTranslation = () => {
  return <h3>Cargando...</h3>;
};

const ShiftReportDataTable = () => {
  const [onRequest, setOnRequest] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [wordModalShow, setWordModalShow] = useState(false);
  const [query, setQuery] = useState({});
  const [amount, setAmount] = useState(0);
  const [selectedCount, setSelectedCount] = useState('');
  const [filters, setFilters] = useState([]);
  const [searchFirstStart, setSearchFirstStart] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [employeeIds, setEmployeeIds] = useState([]);

  const ability = useAbility(AbilityContext);
  const dispatch = useDispatch();

  const handleRequest = async (params, format, responseType = 'json') => {
    setOnRequest(true);
    const empParams = { ...params };
    delete empParams.date_to;
    delete empParams.date_from;
    delete empParams.range_date;
    setQuery(empParams);
    indexEmployeesRequest({
      dispatch,
      params: { ...filters, ...empParams, active: true, is_dt: false },
      format,
      responseType,
      successCallback: response => {
        const { data } = response;
        setAmount(data.metadata.amount);
        setFilteredEmployees(data.data);
      },
      callback: () => setOnRequest(false)
    });
  };

  const handleShiftReportRequest = async (params, format, type = 'json') => {
    const responseType = format === '.docx' ? 'blob' : type;
    setOnRequest(true);
    legalreportShiftReport({
      dispatch,
      params: { filter_by_employee: employeeIds, date_range: dateRange, ...params, paginate: false },
      format,
      responseType,
      successCallback: response => {
        setClearSelectedRows(true);
        if (format === '.xlsx' || format === '.docx') {
          downloadFile(response);
        }
      },
      callback: () => setOnRequest(false)
    });
  };

  const handleMassAction = action => {
    setEmployeeIds(selectedRows.map(employee => employee.id));

    switch (action) {
      case 'download_excel':
        setModalShow(true);
        break;
      case 'download_docx':
        setWordModalShow(true);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
    }
  };
  const handleSelectedRows = item => {
    setSelectedRows(item.selectedRows);
    setSelectedCount(item.selectedCount);
  };
  const handleFilters = params => {
    const empParams = { ...params };
    delete empParams.date_to;
    delete empParams.date_from;
    delete empParams.range_date;
    empParams.filter_ids.push(empParams.filter_id);
    setFilters(empParams);
    setSearchFirstStart(true);
    setDateRange(params.range_date);
    handleRequest(params);
  };
  return (
    <>
      <LegalReportFilter formRequest={handleFilters} />
      <ComponentDataTable
        onRequest={onRequest}
        columns={columns()}
        data={filteredEmployees}
        totalRows={amount}
        withMassActions={ability.can('index', 'ShiftReport')}
        massActions={<ReportActions disabled={!selectedCount || onRequest} handleClick={handleMassAction} />}
        resourceRequest={handleRequest}
        expandableRows
        expandableRowsComponent={<MyExpander dateRange={dateRange} />}
        progressPending={onRequest}
        progressComponent={<LoadingTranslation />}
        searchFirstStart={searchFirstStart}
        selectableRows
        clearSelectedRows={clearSelectedRows}
        onSelectedRowsChange={handleSelectedRows}
      />
      <DefaultModal
        title="Exportar Reporte de Asistencias"
        body="¿Estás seguro que quieres exportar el reporte de asistencia en formato Excel?"
        show={modalShow}
        handleClose={() => setModalShow(false)}
        handleConfirm={() => {
          handleShiftReportRequest(query, '.xlsx', 'text');
          setModalShow(false);
        }}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
      />
      <DefaultModal
        title="Exportar Reporte de Asistencias"
        body="¿Estás seguro que quieres exportar el reporte de asistencia en formato Word?"
        show={wordModalShow}
        handleClose={() => setWordModalShow(false)}
        handleConfirm={() => {
          handleShiftReportRequest(query, '.docx', 'text');
          setWordModalShow(false);
        }}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
      />
    </>
  );
};

export default ShiftReportDataTable;
